import { Building2, Info, RadioTower } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { useUsuario } from '@/lib/auth/contexto';
import { podeNaEmpresa } from '@/lib/auth/permissoes';
import { usePainelCaptura } from '@/lib/captura/api';
import { useEscopoEmpresas } from '@/lib/escopo';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { CabecalhoPagina } from '@/ui/patterns/cabecalho-pagina';
import { Button } from '@/ui/primitives/button';
import { Skeleton } from '@/ui/primitives/skeleton';
import { type EstadoCaptura } from '@fiscoai/contracts';

import { DialogoConsultaChave } from './dialogo-consulta-chave';
import { HistoricoExecucoes } from './historico-execucoes';
import { LinhaEmpresa } from './linha-empresa';

function Carregando() {
  return (
    <div aria-busy="true" aria-label="Carregando a captura" className="grid gap-px">
      {[0, 1].map((i) => (
        <Skeleton key={i} className="h-24 rounded-cartao" />
      ))}
    </div>
  );
}

interface PainelProps {
  painel: ReturnType<typeof usePainelCaptura>;
  escolhidaId: string | null;
  podeExecutar: (empresaId: string) => boolean;
  aoBuscarChave: (estado: EstadoCaptura) => void;
}

/** Esqueleto, erro com nova tentativa, escopo vazio ou a lista das empresas. */
function Painel({ painel, escolhidaId, podeExecutar, aoBuscarChave }: PainelProps) {
  if (painel.isPending) return <Carregando />;
  if (painel.isError)
    return (
      <EstadoVazio
        tom="perigo"
        icone={<RadioTower />}
        titulo="O painel da captura não carregou"
        texto="O servidor não respondeu. Confira a conexão e tente de novo."
        acao={
          <Button tamanho="sm" onClick={() => void painel.refetch()}>
            Tentar de novo
          </Button>
        }
      />
    );
  if (painel.data.empresas.length === 0)
    return (
      <EstadoVazio
        icone={<Building2 />}
        titulo="Nenhuma empresa no escopo"
        texto="Escolha empresas no seletor do topo. Só aparecem as empresas em que você lê documentos."
      />
    );
  return (
    <ul
      aria-label="Empresas na captura"
      className="divide-y divide-borda overflow-hidden rounded-painel border border-borda bg-superficie shadow-baixa"
    >
      {painel.data.empresas.map((estado) => (
        <LinhaEmpresa
          key={estado.empresa.id}
          estado={estado}
          selecionada={estado.empresa.id === escolhidaId}
          podeExecutar={podeExecutar(estado.empresa.id)}
          aoBuscarChave={aoBuscarChave}
        />
      ))}
    </ul>
  );
}

/** O que a distribuição DF-e não entrega (as notas da própria empresa) e por onde elas entram. */
function AvisoNotasProprias() {
  return (
    <p role="note" className="-mt-3 flex items-start gap-2 text-sm text-texto-suave">
      <Info className="mt-0.5 size-4 shrink-0 text-info" aria-hidden />
      <span>
        A SEFAZ não envia as notas emitidas pela própria empresa, nem as que ela emite para si
        mesma. Essas entram por{' '}
        <Link to="/importacao" className="font-medium text-marca hover:underline">
          Importar notas
        </Link>
        .
      </span>
    </p>
  );
}

/** A da URL; sem ela, a única do escopo (o histórico já abre). */
function empresaEscolhida(empresas: readonly EstadoCaptura[], id: string | null) {
  if (id) return empresas.find((e) => e.empresa.id === id) ?? null;
  return empresas.length === 1 ? (empresas[0] ?? null) : null;
}

/**
 * Captura SEFAZ (spec 0006, tela 2): uma linha por empresa do escopo, com a situação e as ações;
 * a empresa escolhida (`?empresa=`) mostra o histórico de execuções abaixo. Com uma empresa só no
 * escopo, o histórico já abre.
 */
export function PaginaCaptura() {
  const usuario = useUsuario();
  const painel = usePainelCaptura(useEscopoEmpresas());
  const [params] = useSearchParams();
  const [porChaveId, setPorChaveId] = useState<string | null>(null);
  const empresas = painel.data?.empresas ?? [];
  const escolhida = empresaEscolhida(empresas, params.get('empresa'));
  const porChave = empresas.find((e) => e.empresa.id === porChaveId);

  return (
    <div className="grid gap-6">
      <CabecalhoPagina
        titulo="Captura SEFAZ"
        descricao="As notas emitidas contra as empresas, buscadas na SEFAZ pela agenda de cada uma. Escolha uma empresa para ver o histórico."
      />
      <AvisoNotasProprias />
      <Painel
        painel={painel}
        escolhidaId={escolhida?.empresa.id ?? null}
        podeExecutar={(id) => podeNaEmpresa(usuario, id, 'documentos.captura.executar')}
        aoBuscarChave={(e) => {
          setPorChaveId(e.empresa.id);
        }}
      />
      {escolhida && <HistoricoExecucoes key={escolhida.empresa.id} estado={escolhida} />}
      {porChave && (
        <DialogoConsultaChave
          estado={porChave}
          aoFechar={() => {
            setPorChaveId(null);
          }}
        />
      )}
    </div>
  );
}
